"use client";

import { Card, LiquidPill } from "components/ui/v2";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ICertificateByUser } from "gql/Certificate-user/queries";
import { Award, Download, ExternalLink } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";

interface CertificateCardV2Props {
  certificate: ICertificateByUser;
  onViewCertificate?: (id: string) => void;
  onDownload?: (id: string) => void;
}

export function CertificateCardV2({
  certificate,
  onViewCertificate,
  onDownload,
}: CertificateCardV2Props) {
  const { t } = useTranslation("common");
  const { certificateHeader, issueDate, id } = certificate;

  const startDate = new Date(certificateHeader.startDate);
  const issueDateObj = issueDate ? new Date(issueDate) : null;

  return (
    <Card
      size="full"
      padding="none"
      shadow="subtle"
      entranceAnimation
      fullHeight
    >
      <article className="flex h-full min-h-[360px] flex-col overflow-hidden">
        <div className="relative h-28 overflow-hidden bg-gradient-to-br from-emerald-50 via-sky-50 to-indigo-50 dark:from-emerald-950/70 dark:via-sky-950/50 dark:to-indigo-950/60">
          {certificateHeader.customImage ? (
            <Image
              src={certificateHeader.customImage}
              alt={certificateHeader.name}
              fill
              sizes="(max-width: 768px) 100vw, 440px"
              className="object-cover"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl border border-white/70 bg-white/60 text-emerald-500 shadow-sm dark:border-zinc-700/70 dark:bg-zinc-900/60 dark:text-emerald-300">
                <Award className="h-8 w-8" />
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/32 via-transparent to-transparent" />
        </div>

        <div className="flex flex-1 flex-col p-3">
          <div className="flex-1">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <LiquidPill
                variant="emerald"
                appearance="tinted"
                shadow="neutral"
                size="xs"
                icon={<Award className="h-3 w-3" />}
                text={t("Certificación")}
              />
            </div>

            <h3 className="line-clamp-2 text-base font-semibold leading-tight text-slate-950 dark:text-white">
              {certificateHeader.name}
            </h3>

            {certificateHeader.subTitle ? (
              <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600 dark:text-zinc-400">
                {certificateHeader.subTitle}
              </p>
            ) : (
              <p className="mt-2 text-sm leading-6 text-slate-400 dark:text-zinc-500">
                {certificateHeader.durationText}
              </p>
            )}
          </div>

          <div className="mt-3 text-[10px] text-slate-500 dark:text-zinc-500">
            <p>
              {t("Inicio")}: {format(startDate, "dd MMM yyyy", { locale: es })}
            </p>
            {issueDateObj && (
              <p>
                {t("Emitido")}:{" "}
                {format(issueDateObj, "dd MMM yyyy", { locale: es })}
              </p>
            )}
          </div>
        </div>

        <div className="mt-3 border-t border-slate-200/70 p-3 dark:border-zinc-800/80">
          <div className="grid grid-cols-2 gap-2">
            <LiquidPill
              as="button"
              type="button"
              variant="indigo"
              appearance="tinted"
              shadow="neutral"
              size="card-cta"
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              text={t("Ver")}
              aria-label={t("Ver certificado")}
              onClick={() => onViewCertificate?.(id)}
            />
            <LiquidPill
              as="button"
              type="button"
              variant="sky"
              appearance="tinted"
              shadow="neutral"
              size="card-cta"
              icon={<Download className="h-3.5 w-3.5" />}
              text={t("Descargar")}
              aria-label={t("Descargar certificado")}
              onClick={() => onDownload?.(id)}
            />
          </div>
        </div>
      </article>
    </Card>
  );
}
